// Responsive.js � central place for responsive utilities + generic table>cards renderer
// Drop this in your components/ folder and import what you need.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import i18n from "./i18n";
const tr = i18n.t.bind(i18n);
// -------------------------------
// Breakpoints + Provider
// -------------------------------
// Default is a simple, sensible set (min-width). Adjust to your design system.
// xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536
const defaultBps = { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 };

const ResponsiveCtx = createContext({
  breakpoints: defaultBps,
  width: typeof window !== 'undefined' ? window.innerWidth : 0,
  height: typeof window !== 'undefined' ? window.innerHeight : 0,
});

export function ResponsiveProvider({ breakpoints = defaultBps, children }) {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const value = useMemo(() => ({ breakpoints, ...size }), [breakpoints, size]);
  return <ResponsiveCtx.Provider value={value}>{children}</ResponsiveCtx.Provider>;
}

export function useResponsive() {
  return useContext(ResponsiveCtx);
}

// -------------------------------
// Core Hooks
// -------------------------------
export function useWindowSize() {
  const { width, height } = useResponsive();
  return { width, height };
}

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    try { mql.addEventListener('change', onChange); } catch { mql.addListener(onChange); }
    setMatches(mql.matches);
    return () => { try { mql.removeEventListener('change', onChange); } catch { mql.removeListener(onChange); } };
  }, [query]);
  return matches;
}

export function useBreakpointName() {
  const { width, breakpoints } = useResponsive();
  const entries = Object.entries(breakpoints).sort((a, b) => a[1] - b[1]);
  let name = entries[0][0];
  for (const [label, min] of entries) {
    if (width >= min) name = label;
  }
  return name; // e.g., 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

export function useIsMobile(maxWidth=599) {
  const { breakpoints } = useResponsive();
  // If maxWidth not provided, treat < sm as mobile
  const cutoff = typeof maxWidth === 'number' ? maxWidth : (breakpoints.sm - 1);
  return useMediaQuery(tr('(max-width: {{cutoff}}px)', {
    cutoff
  }));
}

export function usePrefersReducedMotion() {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

// Convenience booleans
export function useBpFlags() {
  const { width, breakpoints } = useResponsive();
  return {
    isMobile: width < breakpoints.sm,
    isTablet: width >= breakpoints.sm && width < breakpoints.lg,
    isDesktop: width >= breakpoints.lg,
  };
}

// -------------------------------
// Show/Hide helpers
// -------------------------------
export function Show({ above, below, between, children }) {
  const { width, breakpoints } = useResponsive();
  const aboveOk = above ? width >= breakpoints[above] : true;
  const belowOk = below ? width < breakpoints[below] : true;
  const betweenOk = between ? (width >= breakpoints[between[0]] && width < breakpoints[between[1]]) : true;
  const ok = between ? betweenOk : (aboveOk && belowOk);
  return ok ? <>{children}</> : null;
}

export function Hide(props) {
  return <Show {...props}>{null}</Show>;
}

export function OnlyMobile({ children }) {
  const { breakpoints } = useResponsive();
  return <Show below="sm">{children}</Show>;
}
export function OnlyTablet({ children }) {
  return <Show between={["sm", "lg"]}>{children}</Show>;
}
export function OnlyDesktop({ children }) {
  return <Show above="lg">{children}</Show>;
}

// -------------------------------
// Switcher to render different trees per breakpoint
// -------------------------------
export function ResponsiveSwitch({ desktop, mobile, tablet = desktop, below = 'sm', above = 'lg' }) {
  const { width, breakpoints } = useResponsive();
  if (width < breakpoints[below]) return <>{mobile}</>;
  if (width >= breakpoints[above]) return <>{desktop}</>;
  return <>{tablet}</>;
}

// ========== Generic: make an HTML <table> render like cards on mobile ==========
export function useCardifyTable(tableRef, { className = 'cardify', observe = true } = {}) {
  useEffect(() => {
    const el = tableRef?.current;
    if (!el) return;

    const thead = el.querySelector('thead');
    const tbody = el.querySelector('tbody');
    if (!thead || !tbody) return;

    const getHeaders = () =>
      Array.from(thead.querySelectorAll('th')).map(th => (th.textContent || '').trim());

   const norm = (s) => (s || "").replace(/\u00A0/g, " ").trim();

   const hasMeaningfulChild = (td) => {
     // buttons/selects/inputs count as value
     if (td.querySelector("button, select, input, textarea")) return true;
     // icons/images count as value
     if (td.querySelector("svg, img")) return true;
     // links count only if they have real content
     const a = td.querySelector("a");
     if (a) {
       const tx = norm(a.textContent);
       const href = (a.getAttribute("href") || "").trim();
       if (tx) return true;
       if (href && href !== "#" && href !== "tel:" && href !== "mailto:") return true;
     }
     return false;
   };

    const apply = () => {
      const headers = getHeaders(); // ✅ always current (plan/language changes)
      el.classList.add(className);

      Array.from(tbody.rows).forEach((tr) => {
        Array.from(tr.cells).forEach((td, i) => {
          td.setAttribute('data-label', headers[i] || '');

         const empty = !norm(td.textContent) && !hasMeaningfulChild(td);
         if (empty) td.setAttribute("data-empty", "1");
         else td.removeAttribute("data-empty");
        });
      });
    };

    apply();

    if (!observe) return;

    const obs = new MutationObserver(apply);
    try {
      // ✅ watch both body + header, so labels update when columns/titles change
      obs.observe(tbody, { childList: true, subtree: true, characterData: true });
      obs.observe(thead, { childList: true, subtree: true, characterData: true });
    } catch {}

    return () => { try { obs.disconnect(); } catch {} };
  }, [tableRef, className, observe]);
}

